import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Drawer, List, ListItem, ListItemButton, ListItemIcon, ListItemText,
  Toolbar, Divider, Typography, Box, Collapse, Tooltip, Badge,
} from '@mui/material';
import DashboardIcon from '@mui/icons-material/Dashboard';
import ClientsIcon from '@mui/icons-material/Business';
import ProjectsIcon from '@mui/icons-material/Folder';
import DocsIcon from '@mui/icons-material/Description';
import EconomicIcon from '@mui/icons-material/AccountBalance';
import PlanningIcon from '@mui/icons-material/Timeline';
import ReportsIcon from '@mui/icons-material/Assessment';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import ReceiptIcon from '@mui/icons-material/Receipt';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import PrecisionManufacturingIcon from '@mui/icons-material/PrecisionManufacturing';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import PeopleIcon from '@mui/icons-material/People';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import BuildIcon from '@mui/icons-material/Build';
import AssignmentIcon from '@mui/icons-material/Assignment';
import ChatIcon from '@mui/icons-material/Chat';
import { useAuth } from '../../contexts/AuthContext';
import { useChat } from '../../contexts/ChatContext';

const DRAWER_WIDTH = 280;
const MINI_WIDTH = 60;

const allMenuItems = [
  { id: 'dashboard', label: 'Dashboard', icon: <DashboardIcon />, path: '/' },
  { id: 'clients', label: 'Clientes', icon: <ClientsIcon />, path: '/clients' },
  { id: 'suppliers', label: 'Proveedores', icon: <LocalShippingIcon />, path: '/suppliers' },
  { id: 'projects', label: 'Proyectos', icon: <ProjectsIcon />, path: '/projects' },
  { id: 'chats', label: 'Chats', icon: <ChatIcon />, path: '/chats' },
  { id: 'documents', label: 'Documentos', icon: <DocsIcon />, path: '/documents' },
  {
    id: 'economic', label: 'Economico', icon: <EconomicIcon />,
    children: [
      { id: 'budget', label: 'Presupuestos', icon: <ReceiptIcon />, path: '/economic/budget' },
      { id: 'invoices', label: 'Facturas', icon: <ReceiptIcon />, path: '/economic/invoices' },
      { id: 'cashflow', label: 'Flujo de Caja', icon: <TrendingUpIcon />, path: '/economic/cashflow' },
    ],
  },
  { id: 'planning', label: 'Planning', icon: <PlanningIcon />, path: '/planning' },
  { id: 'tasks', label: 'Tareas', icon: <AssignmentIcon />, path: '/tasks' },
  { id: 'reports', label: 'Informes', icon: <ReportsIcon />, path: '/reports' },
  { id: 'tools', label: 'Herramientas', icon: <BuildIcon />, path: '/tools' },
  { id: 'divider-admin', divider: true, adminOnly: true },
  { id: 'users', label: 'Usuarios', icon: <PeopleIcon />, path: '/users', adminOnly: true },
  { id: 'settings', label: 'Configuracion', icon: <AdminPanelSettingsIcon />, path: '/settings', adminOnly: true },
];

export default function Sidebar({ open: openProp, mobile = false, onClose }) {
  // En el movil el menu siempre se pinta desplegado, dentro de un panel temporal
  const open = mobile ? true : openProp;
  const navigate = useNavigate();
  const location = useLocation();
  const { isAdmin, allowedMenuIds, company } = useAuth();
  const [openMenus, setOpenMenus] = React.useState({ engineering: false, economic: false });
  const logo = company?.company_logo;
  const companyName = company?.company_name;

  const width = open ? DRAWER_WIDTH : MINI_WIDTH;

  // Contador de no leidos en la entrada Chats, como el globo de WhatsApp
  const { unread } = useChat();

  const go = (path) => {
    navigate(path);
    if (mobile && onClose) onClose();
  };

  // Filter by adminOnly first, then by category permissions
  const menuItems = allMenuItems
    .filter((item) => !item.adminOnly || isAdmin)
    .map((item) => (item.id === 'chats'
      ? { ...item, icon: <Badge badgeContent={unread} color="success" max={99}>{item.icon}</Badge> }
      : item))
    .reduce((acc, item) => {
      if (!allowedMenuIds) {
        // null = show everything (admin, or user with no category assigned)
        acc.push(item);
      } else if (item.divider) {
        // dividers are structural, skip if non-admin
        // (admin-only divider already excluded above)
      } else if (item.children) {
        const visibleChildren = item.children.filter((c) => allowedMenuIds.includes(c.id));
        if (allowedMenuIds.includes(item.id) || visibleChildren.length > 0) {
          acc.push({ ...item, children: visibleChildren });
        }
      } else if (allowedMenuIds.includes(item.id)) {
        acc.push(item);
      }
      return acc;
    }, []);

  const toggleMenu = (id) => {
    setOpenMenus((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const renderMenuItem = (item) => {
    if (item.divider) {
      return <Divider key={item.id} sx={{ my: 1 }} />;
    }
    if (item.children) {
      // In mini mode: show only the parent icon, navigate to first child on click
      if (!open) {
        return (
          <ListItem key={item.id} disablePadding sx={{ display: 'block' }}>
            <Tooltip title={item.label} placement="right">
              <ListItemButton
                onClick={() => go(item.children[0]?.path || '/')}
                sx={{ justifyContent: 'center', px: 1 }}
              >
                <ListItemIcon sx={{ minWidth: 0, justifyContent: 'center' }}>{item.icon}</ListItemIcon>
              </ListItemButton>
            </Tooltip>
          </ListItem>
        );
      }
      return (
        <React.Fragment key={item.id}>
          <ListItem disablePadding>
            <ListItemButton onClick={() => toggleMenu(item.id)}>
              <ListItemIcon>{item.icon}</ListItemIcon>
              <ListItemText primary={item.label} />
              {openMenus[item.id] ? <ExpandLess /> : <ExpandMore />}
            </ListItemButton>
          </ListItem>
          <Collapse in={openMenus[item.id]} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {item.children.map((child) => (
                <ListItemButton
                  key={child.id}
                  sx={{ pl: 4 }}
                  selected={location.pathname === child.path}
                  onClick={() => go(child.path)}
                >
                  <ListItemIcon>{child.icon}</ListItemIcon>
                  <ListItemText primary={child.label} />
                </ListItemButton>
              ))}
            </List>
          </Collapse>
        </React.Fragment>
      );
    }

    if (!open) {
      return (
        <ListItem key={item.id} disablePadding sx={{ display: 'block' }}>
          <Tooltip title={item.label} placement="right">
            <ListItemButton
              selected={location.pathname === item.path}
              onClick={() => go(item.path)}
              sx={{ justifyContent: 'center', px: 1 }}
            >
              <ListItemIcon sx={{ minWidth: 0, justifyContent: 'center' }}>{item.icon}</ListItemIcon>
            </ListItemButton>
          </Tooltip>
        </ListItem>
      );
    }

    return (
      <ListItem key={item.id} disablePadding>
        <ListItemButton
          selected={location.pathname === item.path}
          onClick={() => go(item.path)}
        >
          <ListItemIcon>{item.icon}</ListItemIcon>
          <ListItemText primary={item.label} />
        </ListItemButton>
      </ListItem>
    );
  };

  return (
    <Drawer
      variant={mobile ? 'temporary' : 'permanent'}
      open={mobile ? openProp : true}
      onClose={onClose}
      ModalProps={{ keepMounted: true }}
      sx={{
        width: mobile ? 0 : width,
        flexShrink: 0,
        transition: 'width 0.2s',
        '& .MuiDrawer-paper': {
          width: mobile ? DRAWER_WIDTH : width,
          maxWidth: '85vw',
          boxSizing: 'border-box',
          overflowX: 'hidden',
          transition: 'width 0.2s',
        },
      }}
    >
      <Toolbar sx={{ justifyContent: open ? 'flex-start' : 'center', px: open ? 2 : 1 }}>
        {open ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
            {logo ? (
              <Box component="img" src={logo} alt="Logo"
                sx={{ height: 36, maxWidth: 160, objectFit: 'contain' }} />
            ) : (
              <>
                <PrecisionManufacturingIcon color="primary" sx={{ fontSize: 32 }} />
                <Typography variant="h6" noWrap color="primary" fontWeight="bold">
                  {companyName || 'GPI'}
                </Typography>
              </>
            )}
          </Box>
        ) : (
          logo ? (
            <Tooltip title={companyName || ''} placement="right">
              <Box component="img" src={logo} alt="Logo"
                sx={{ height: 28, maxWidth: 40, objectFit: 'contain' }} />
            </Tooltip>
          ) : (
            <PrecisionManufacturingIcon color="primary" sx={{ fontSize: 28 }} />
          )
        )}
      </Toolbar>
      <Divider />
      <List sx={{ pt: 0 }}>
        {menuItems.map(renderMenuItem)}
      </List>
    </Drawer>
  );
}
